import { Button, Col, DatePicker, Divider, Input, Radio, RadioChangeEvent, Row, Select, Typography } from 'antd';
import React, { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import IconMarker from '../../../assets/icon/locate2.png'
import RedMarker from '../../../assets/icon/red_marker.png'
import styles from '../Style/ValidationPanel.module.scss'
import { MarkerItem } from '../../../typings/home';
import { DatasetRecord } from '../Store/dataTypes';
import { IDatasetBrief, IEnrichedMetadata, IGetMetadata } from '../../../typings/metadata';
import { StoreContext } from '../../../store/store';
import Api from '../../../api/Api';
import { featureCollection, point } from '@turf/helpers';
import nearestPoint from '@turf/nearest-point';
import TimeSeries from './ValidationPlot/TimeSeries';
import RosePlot from './ValidationPlot/RosePlot';
import ScatterPlot from './ValidationPlot/ScatterPlot';
import { IItemsValidation } from '../../../typings/analytics';
import moment, { Moment } from 'moment';
import { DownloadCheckboxKey } from '../../../generic/common';
import { isEmpty } from 'underscore';

interface Props {
    activePoint?: MarkerItem;
    activePointMetadata?: DatasetRecord
    validInSituPoint?: boolean
    onChangePointLocation: (coords: number[], forceChange?: boolean) => void
    isInsituAvailable: boolean
}

const InSituTab: React.FC<Props> = ({
    activePoint,
    activePointMetadata,
    validInSituPoint,
    onChangePointLocation,
    isInsituAvailable,
}) => {
    const [activePlot, setActivePlot] = useState(0);
    const [listParameters, setListParameters] = useState<IItemsValidation[]>([])
    const [enrichedMetadata, setEnrichedMetadata] = useState<IEnrichedMetadata[]>([])

    const context = useContext(StoreContext)
    const prevMetadataRef = useRef<IGetMetadata>();

    const handleChangeCategoryKey = useCallback((value) => {
      if(activePoint){
        context.setActiveCategoryKey((prevState: { [key: string]: boolean }) => ({
          ...prevState,
          [activePoint.id]: value
        }))
      }
    }, [activePoint])

    const category = useMemo(() => {
      return  activePoint && !isEmpty(context.activeCategoryKey) ? context.activeCategoryKey[activePoint.id] : DownloadCheckboxKey.SW;
    }, [context.activeCategoryKey, activePoint])

    const onChangeActivePlot = (e: RadioChangeEvent) => {
        setActivePlot(e.target.value);
    };

    const getParameters = async () => {
      try {
        const parametersData = await Api.insituPoint.getParametersInsitu();
        if(parametersData?.length > 0){
          const enrichedData = parametersData.map((item : IItemsValidation) => ({ ...item }));

          enrichedData.forEach((item : IItemsValidation) => {
            const match = activePointMetadata?.metadata?.ItemsInfo.find(compareItem => compareItem.ShortName === item.name);
            if (match) {
              item.details = match.Name;
            }
          });

          setListParameters(enrichedData ?? [])
        }
        else {
          setListParameters([])
        }
      } catch (error) {
        console.error('Error fetching parameters:', error);
      }
    }

    useEffect(() => {
      getParameters()
    }, [])


    const activeInSituPoint = useMemo(() => {
        if (context.inSituPoints && context.inSituPoints.length > 0 && activePoint?.coordinates) {
            const from = point(activePoint.coordinates);
            const to = context.inSituPoints.map(
                (item) => point(item.position.slice(0, 2), { source: item.source, name: item.name, startDate: item.startDate, endDate: item.endDate })
            );
            const features = featureCollection(to);
            const nearest = nearestPoint(from, features);
            if (nearest) {
                return {
                    name: nearest.properties?.name || '',
                    coordinates: nearest.geometry.coordinates as [number, number],
                    source: nearest.properties?.source,
                    dateRange: [moment(nearest.properties.startDate), moment(nearest.properties.endDate)] as [moment.Moment, moment.Moment]
                };
            }
        }
        return {
            name: '',
            source: '',
            coordinates: []
        }
    }, [context.inSituPoints, activePoint?.coordinates])

    useEffect(() => {
        if (activePoint && activePointMetadata && activePointMetadata?.userGroupPermissions && validInSituPoint) {
            const currentMetadata = activePointMetadata?.metadata;
            if (prevMetadataRef.current?.DatasetId !== currentMetadata?.DatasetId) {
                context.setPreviewAnalytics([
                    ...context.previewAnalytics,
                    {
                        datasetId: currentMetadata!.DatasetId,
                        ItemsInfo: currentMetadata!.ItemsInfo,
                        DefaultDirectionShortName: currentMetadata!.DefaultDirectionShortName,
                        DefaultValueShortName: currentMetadata!.DefaultValueShortName,
                        coordinates: activePoint.coordinates,
                    },
                ]);
                triggerFullExtraction(currentMetadata!.DatasetId, activePoint.coordinates.join());
                prevMetadataRef.current = currentMetadata;
            }
        }
    }, [validInSituPoint, activePointMetadata?.metadata, activePointMetadata?.userGroupPermissions])

    const onChangeMatchInsitu = () => {
        if (activePoint?.coordinates) {
            onChangePointLocation(
                [Number(activeInSituPoint.coordinates[0]), Number(activeInSituPoint.coordinates[1])]
            )
        }
    }

    const triggerFullExtraction = async (
        datasetId: string,
        coordinatesString: string
    ) => {
        const datasetTask = enrichedMetadata.find(
            (task: IEnrichedMetadata) =>
                task.DatasetId === datasetId &&
                task.CoordinatesString === coordinatesString
        );
        const metadata = activePointMetadata?.metadata;
        if (metadata && datasetTask === undefined) {
            try {
                const createFullExtractionTasks =
                    await Api.service.createFullExtractionTasks(
                        [metadata],
                        activePoint?.coordinates!,
                        activePoint?.label!,
                        activePointMetadata?.depth?.toString()!,
                        activePointMetadata?.cellSize?.toString()!,
                        context.datasetBriefs,
                        context.fwePoints,
                    );
                const enrichedMDs = context.enrichedMetadata.concat(
                    createFullExtractionTasks
                );
                setEnrichedMetadata(enrichedMDs)
            } catch (error) {
                console.log(error)
            }
        }
    }

    return (
        <div className={styles.insituWrapper}>
            <div className={styles.insituSidebar}>
                <Radio.Group onChange={onChangeActivePlot} value={activePlot} size="small">
                    <Radio value={0}>Time Series Plot</Radio>
                    <Radio value={1}>Scatter Plot</Radio>
                    <Radio value={2} disabled >Rose Plot</Radio>
                </Radio.Group>
                <Divider />
                <div>
                    <Row gutter={12}>
                        <Col span={24}>
                            <img src={IconMarker} className={styles.insituIcon} />
                            <Typography.Text strong>{activePoint?.label}</Typography.Text>
                        </Col>
                    </Row>
                    <Row gutter={8} className={styles.insituCoordinates}>
                        <Col span={10}>
                            <Input prefix={'Long'} disabled value={activePoint?.coordinates[0]} />
                        </Col>
                        <Col span={10}>
                            <Input prefix={'Lat'} disabled value={activePoint?.coordinates[1]} />
                        </Col>
                    </Row>
                    <Row>
                        <Button
                            size='small'
                            type='primary'
                            onClick={onChangeMatchInsitu}
                            disabled={activeInSituPoint.coordinates.join() === activePoint?.coordinates.join() || !isInsituAvailable}
                        >
                            Change to match in situ
                        </Button>

                    </Row>
                </div>
                <Divider />
                <div>
                    <Row gutter={12}>
                        <Col span={24}>
                            <img src={RedMarker} className={styles.insituMeasuresIcon} />
                            <Typography.Text strong>{activeInSituPoint.name}</Typography.Text>
                        </Col>
                    </Row>
                    <Row>
                        <Typography.Text className={styles.insituButtonFontSize}>This is closest measurement station</Typography.Text>
                    </Row>
                    <Row gutter={8} className={styles.insituCoordinates}>
                        <Col span={12}>
                            <Typography.Text className={styles.insituColor}>Long : {activeInSituPoint.coordinates[0]}</Typography.Text>
                        </Col>
                        <Col span={12}>
                            <Typography.Text className={styles.insituColor}>Lat : {activeInSituPoint.coordinates[1]}</Typography.Text>
                        </Col>
                    </Row>
                    <Row gutter={8} className={styles.insituCoordinates}>
                        <Col span={10}>
                            <Typography.Text className={styles.insituColor}>Provider : {activeInSituPoint.source}</Typography.Text>
                        </Col>
                    </Row>
                </div>
            </div>
            <div className={styles.insituChartWrapper}>
                {!validInSituPoint && (
                    <div className={styles.warningMessageWrapper}>
                        <div className={styles.warningBox}>
                            {context.showInSituPoints && !isInsituAvailable && (
                                <>
                                    <b>In situ validation not available</b>
                                    <br />
                                    <p>
                                        Validation against an in situ station is not possible as there are no in
                                        situ measurements within the domain of the dataset
                                    </p>
                                </>
                            )}
                            {context.showInSituPoints && isInsituAvailable && (
                                <>
                                    <b>In situ validation not available</b>
                                    <br />
                                    <p>
                                        To view the in situ validation you can change to a new point to match the location
                                        of the insitu station
                                    </p>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end'
                                    }}>
                                        <Button
                                            type='text'
                                            onClick={onChangeMatchInsitu}>
                                            <b>Change to a new point to match in situ</b>
                                        </Button>
                                    </div>
                                </>
                            )}
                            {!context.showInSituPoints && (
                                <>
                                    <b>Please activate Insitu measurement on sidebar</b>
                                    <br />
                                    <p>
                                        To view the in situ validation you must activate Insitu measurement on sidebar
                                    </p>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end'
                                    }}>
                                        <Button
                                            type='text'
                                            onClick={() => context.setShowInSituPoints(true)}>
                                            <b>Activate the Insitu measurement</b>
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
                <div style={{ display: activePlot === 0 ? 'block' : 'none' }}>
                    {<TimeSeries
                        activeCategoryKey={category as string}
                        setActiveCategory={handleChangeCategoryKey}
                        listParameters={listParameters}
                        activePointMetadata={activePointMetadata}
                        validInSituPoint={validInSituPoint && context.showInSituPoints}
                        enrichedMetadata={enrichedMetadata}
                        activePoint={activePoint}
                        tooltipName={{
                            insitu: activeInSituPoint.name,
                            point: activePoint?.label
                        }}
                        dateRange={activeInSituPoint.dateRange}
                        activeTab={activePlot}
                    />}
                </div>
                <div style={{ display: activePlot === 1 ? 'block' : 'none' }}>
                    <ScatterPlot
                        activeCategoryKey={category as string}
                        setActiveCategory={handleChangeCategoryKey}
                        activePointMetadata={activePointMetadata}
                        validInSituPoint={validInSituPoint && context.showInSituPoints}
                        enrichedMetadata={enrichedMetadata}
                        activePoint={activePoint}
                        insituName={activeInSituPoint.name}
                        listParameters={listParameters}
                        dateRange={activeInSituPoint.dateRange}
                        activeTab={activePlot}
                    />
                </div>
                <div style={{ display: activePlot === 2 ? 'block' : 'none' }}>
                    <RosePlot
                        activeCategoryKey={category as string}
                        setActiveCategory={handleChangeCategoryKey}
                        activePointMetadata={activePointMetadata}
                        validInSituPoint={validInSituPoint && context.showInSituPoints}
                        enrichedMetadata={enrichedMetadata}
                        activePoint={activePoint}
                        insituName={activeInSituPoint.name}
                        listParameters={listParameters}
                        dateRange={activeInSituPoint.dateRange}
                        activeTab={activePlot}
                    />
                </div>
            </div>
        </div>
    );
}

export default InSituTab;
